import {
  subscribeBazaarDMMessages,
  subscribeBazaarDMOffers,
  subscribeBazaarDMThreadState,
  subscribeBazaarDMReadState,
  type BazaarDMMessage,
  type BazaarDMOffer,
} from './bazaarDMs';

export type BazaarDMThreadSnapshot = {
  uid: string;
  threadId: string;
  threadReady: boolean;
  threadExists: boolean;
  messagesReady: boolean;
  offersReady: boolean;
  messages: BazaarDMMessage[];
  offers: BazaarDMOffer[];
  hiddenBefore: number;
};

type Entry = {
  snapshot: BazaarDMThreadSnapshot;
  listeners: Set<() => void>;
  stopThread: (() => void) | null;
  stopMessages: (() => void) | null;
  stopOffers: (() => void) | null;
  stopRead: (() => void) | null;
  allMessages: BazaarDMMessage[];
  lastUsedAt: number;
};

const entries = new Map<string, Entry>();
const MAX_WARM_THREADS = 8;

const keyFor = (uid: string, threadId: string) => `${uid}::${threadId}`;

function blank(uid: string, threadId: string): BazaarDMThreadSnapshot {
  return {
    uid,
    threadId,
    threadReady: false,
    threadExists: false,
    messagesReady: false,
    offersReady: false,
    messages: [],
    offers: [],
    hiddenBefore: 0,
  };
}

function emit(entry: Entry) {
  entry.listeners.forEach((listener) => {
    try {
      listener();
    } catch {}
  });
}

function stopChildSubscriptions(entry: Entry) {
  entry.stopMessages?.();
  entry.stopMessages = null;
  entry.stopOffers?.();
  entry.stopOffers = null;
  entry.stopRead?.();
  entry.stopRead = null;
}

function stopEntry(entry: Entry) {
  entry.stopThread?.();
  entry.stopThread = null;
  stopChildSubscriptions(entry);
}

function prune(exceptKey: string) {
  if (entries.size <= MAX_WARM_THREADS) return;
  const victims = [...entries.entries()]
    .filter(([key]) => key !== exceptKey)
    .sort((a, b) => a[1].lastUsedAt - b[1].lastUsedAt);

  while (entries.size > MAX_WARM_THREADS && victims.length) {
    const [key, entry] = victims.shift()!;
    stopEntry(entry);
    entries.delete(key);
  }
}

function ensureChildSubscriptions(entry: Entry) {
  const { threadId } = entry.snapshot;
  if (!threadId || !entry.snapshot.threadExists) return;

  if (!entry.stopMessages) {
    entry.stopMessages = subscribeBazaarDMMessages(
      threadId,
      (messages) => {
        entry.allMessages = messages;
        const visible = messages.filter((message) => Boolean(message.messageType) || Number(message.createdAt || 0) > Number(entry.snapshot.hiddenBefore || 0));
        entry.snapshot = { ...entry.snapshot, messages: visible, messagesReady: true };
        entry.lastUsedAt = Date.now();
        emit(entry);
      },
      () => {
        entry.snapshot = { ...entry.snapshot, messagesReady: true };
        emit(entry);
      },
    );
  }

  if (!entry.stopOffers) {
    entry.stopOffers = subscribeBazaarDMOffers(
      threadId,
      (offers) => {
        entry.snapshot = { ...entry.snapshot, offers, offersReady: true };
        entry.lastUsedAt = Date.now();
        emit(entry);
      },
      () => {
        entry.snapshot = { ...entry.snapshot, offersReady: true };
        emit(entry);
      },
    );
  }


  if (!entry.stopRead) {
    entry.stopRead = subscribeBazaarDMReadState(
      entry.snapshot.uid,
      threadId,
      (readState) => {
        const hiddenBefore = Number(readState.hiddenBefore || 0);
        const visible = entry.allMessages.filter((message) => Boolean(message.messageType) || Number(message.createdAt || 0) > hiddenBefore);
        entry.snapshot = { ...entry.snapshot, hiddenBefore, messages: visible };
        entry.lastUsedAt = Date.now();
        emit(entry);
      },
      () => undefined,
    );
  }
}

export function startBazaarDMThreadStore(uid: string, threadId: string) {
  const cleanUid = String(uid || '').trim();
  const cleanThreadId = String(threadId || '').trim();
  if (!cleanUid || !cleanThreadId) return;

  const key = keyFor(cleanUid, cleanThreadId);
  let entry = entries.get(key);
  if (!entry) {
    entry = {
      snapshot: blank(cleanUid, cleanThreadId),
      listeners: new Set(),
      stopThread: null,
      stopMessages: null,
      stopOffers: null,
      stopRead: null,
      allMessages: [],
      lastUsedAt: Date.now(),
    };
    entries.set(key, entry);
  }

  entry.lastUsedAt = Date.now();
  if (entry.stopThread) {
    ensureChildSubscriptions(entry);
    prune(key);
    return;
  }

  entry.stopThread = subscribeBazaarDMThreadState(
    cleanThreadId,
    cleanUid,
    (state) => {
      const exists = Boolean(state?.exists);
      entry!.snapshot = {
        ...entry!.snapshot,
        threadReady: true,
        threadExists: exists,
        messagesReady: exists ? entry!.snapshot.messagesReady : true,
        offersReady: exists ? entry!.snapshot.offersReady : true,
        messages: exists ? entry!.snapshot.messages : [],
        offers: exists ? entry!.snapshot.offers : [],
        hiddenBefore: exists ? entry!.snapshot.hiddenBefore : 0,
      };
      entry!.lastUsedAt = Date.now();

      if (exists) {
        ensureChildSubscriptions(entry!);
      } else {
        stopChildSubscriptions(entry!);
      }
      emit(entry!);
    },
    () => {
      entry!.snapshot = {
        ...entry!.snapshot,
        threadReady: true,
        threadExists: false,
        messagesReady: true,
        offersReady: true,
        messages: [],
        offers: [],
        hiddenBefore: 0,
      };
      stopChildSubscriptions(entry!);
      emit(entry!);
    },
  );

  prune(key);
}

export function getBazaarDMThreadSnapshot(uid: string, threadId: string): BazaarDMThreadSnapshot {
  const cleanUid = String(uid || '').trim();
  const cleanThreadId = String(threadId || '').trim();
  const entry = entries.get(keyFor(cleanUid, cleanThreadId));
  if (entry) {
    entry.lastUsedAt = Date.now();
    return entry.snapshot;
  }
  return blank(cleanUid, cleanThreadId);
}

export function subscribeBazaarDMThreadStore(
  uid: string,
  threadId: string,
  listener: () => void,
) {
  const cleanUid = String(uid || '').trim();
  const cleanThreadId = String(threadId || '').trim();
  if (!cleanUid || !cleanThreadId) return () => undefined;

  startBazaarDMThreadStore(cleanUid, cleanThreadId);
  const entry = entries.get(keyFor(cleanUid, cleanThreadId));
  if (!entry) return () => undefined;

  entry.listeners.add(listener);
  return () => entry.listeners.delete(listener);
}
